"use client";

import useSearchRouter from "@/app/hooks/useSearchRouter";
import { STAR_RATING_ATTRIBUTE_NAME } from "@/app/search/facets/facetBar";
import StarRating from "@/components/design-system/star-rating";
import { setRefinementToClear } from "@/store/clearRefinementSlice";
import { useSearchParams } from "next/navigation";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import styles from "./filters.module.scss";

type RefinementsProps = {
  excludedAttributes?: string[];
  setCategoryResetSignal: Dispatch<SetStateAction<number>>;
  setSelectedValues: Dispatch<SetStateAction<Record<string, Record<string, boolean>>>>;
  unselectAllFacetsClientSideCallback?: () => void;
  indexName?: string;
};

export default function Refinements({
  excludedAttributes,
  setCategoryResetSignal,
  setSelectedValues,
  unselectAllFacetsClientSideCallback,
  indexName,
}: RefinementsProps) {
  const { removeAllRefinements, debouncedModifyFacets } = useSearchRouter(indexName);

  const searchParams = useSearchParams();
  const dispatch = useDispatch();

  // Filter out general_rating refinements for separate display
  const [generalRatingValues, setGeneralRatingValues] = useState<string[]>([]);
  const [otherRefinements, setOtherRefinements] = useState<[string, string[]][]>([]);
  const [refinementsToRemove, setRefinementsToRemove] = useState<RefinementRecord[]>([]);

  // Listen to parameter changing to know what the refinements should be.
  useEffect(() => {
    const refinements: Record<string, string[]> = {};
    const query = new URLSearchParams(searchParams?.toString() || "");

    // Parse facet params
    query.forEach((value, key) => {
      if ((key.startsWith("facet.") || key.startsWith("attributes.")) && key !== "facet.category") {
        const facetName = key.replace("facet.", "") || key.replace("attributes.", "");
        if (!refinements[facetName]) {
          refinements[facetName] = [];
        }
        if (value.includes("~")) {
          const splitValues = value.split("~");
          splitValues.forEach((value) => {
            refinements[facetName].push(value);
          });
        } else {
          refinements[facetName].push(value);
        }
      }
    });
    const generalRatinRefinments = refinements[STAR_RATING_ATTRIBUTE_NAME] || [];
    const otherRefinementsTemp = Object.entries(refinements).filter(([facet]) => facet !== STAR_RATING_ATTRIBUTE_NAME);
    setGeneralRatingValues(generalRatinRefinments);
    setOtherRefinements(otherRefinementsTemp);
  }, [searchParams]);

  const resetCategoryFilters = useCallback(() => {
    if (setCategoryResetSignal) {
      setCategoryResetSignal((prev) => prev + 1); // Increment to trigger reset
    }
  }, [setCategoryResetSignal]);

  const debouncedRemoveFacets = useDebouncedCallback((facetsToRemove: RefinementRecord[]) => {
    const removedOnly: Record<string, Record<string, boolean>> = {};
    for (const facet of facetsToRemove) {
      let modifyFacet;
      if (removedOnly[facet.facetCategoryName]) {
        modifyFacet = removedOnly[facet.facetCategoryName];
        modifyFacet[facet.facetValueName] = false;
      } else {
        removedOnly[facet.facetCategoryName] = {};
        removedOnly[facet.facetCategoryName][facet.facetValueName] = false;
      }
    }
    debouncedModifyFacets(removedOnly);
  }, 1000);

  const removeFacet = useCallback(
    (facetName: string, value: string, isAll: boolean) => {
      const addRefinementToRemove = (facetRecord: RefinementRecord): RefinementRecord[] => {
        const newArray = [...refinementsToRemove]; // Copy the array
        newArray.push(facetRecord);
        setRefinementsToRemove(newArray);
        return newArray;
      };

      if (isAll) {
        // Clear All
        // Clear All - immediate execution, no debounce
        setSelectedValues({});
        removeAllRefinements();
        resetCategoryFilters();
        if (unselectAllFacetsClientSideCallback) {
          unselectAllFacetsClientSideCallback();
        }
        // Remove client side refinements
        setGeneralRatingValues([]);
        setOtherRefinements([]);
        return;
      } else {
        // Clear individual refinement
        const removedOnly = {
          [facetName]: { [value]: false },
        };
        const refinementToRemove: RefinementRecord = {
          facetCategoryName: facetName,
          facetValueName: value,
        };
        const currentRefinementsToRemove = addRefinementToRemove(refinementToRemove);
        // Use the refinement clear slice to tell the client side facet option to uncheck.
        dispatch(setRefinementToClear(value));

        // Individual facet removal - debounced
        debouncedRemoveFacets(currentRefinementsToRemove);

        setSelectedValues((prev) => {
          const prevFacetValues = prev[facetName] || {};
          const { [value]: _, ...remainingFacetValues } = prevFacetValues;
          const updated =
            Object.keys(remainingFacetValues).length > 0
              ? { ...prev, [facetName]: remainingFacetValues }
              : Object.fromEntries(Object.entries(prev).filter(([k]) => k !== facetName));

          debouncedModifyFacets(removedOnly);
          return updated;
        });

        // Remove refinement client side immediately
        if (facetName === STAR_RATING_ATTRIBUTE_NAME) {
          const newStarRatingRefinementList = generalRatingValues.filter((refinement) => refinement !== value);
          setGeneralRatingValues(newStarRatingRefinementList); // Remove client side refinement
        } else {
          // Filter out the selected facet client-side immediately and remove.
          setOtherRefinements(
            otherRefinements.map((e) => {
              if (e[0] === facetName && e[1]) {
                e[1] = e[1].filter((facet) => facet !== value); // Filter out the selected facet
              }
              return e;
            }),
          );
        }
      }
    },
    [
      debouncedModifyFacets,
      debouncedRemoveFacets,
      dispatch,
      generalRatingValues,
      otherRefinements,
      refinementsToRemove,
      removeAllRefinements,
      resetCategoryFilters,
      setSelectedValues,
      unselectAllFacetsClientSideCallback,
    ],
  );

  // Find the lowest general_rating value
  const getLowestGeneralRating = useCallback(() => {
    if (generalRatingValues) {
      const values = generalRatingValues;
      if (!values || values.length === 0) return null;
      const numericValues = values.map((v) => parseFloat(v)).filter((n) => !isNaN(n));
      if (numericValues.length === 0) return null;
      return Math.min(...numericValues);
    }
    return -1;
  }, [generalRatingValues]);

  const lowestGeneralRating = useMemo(() => getLowestGeneralRating(), [getLowestGeneralRating]);

  const shouldExcludeRefinement = (facet: string, value: string) => {
    if (excludedAttributes?.includes(facet)) return true;
    return value === "≤ 5";
  };

  return otherRefinements.length > 0 || generalRatingValues.length > 0 ? (
    <div className={styles.refinementContainer}>
      <div className={styles.refinementHeaderContainer}>
        <span className={styles.refinementsHeading}>Your refinements</span>
        <button className={`p-0 ${styles.clearAllBtn}`} onClick={() => removeFacet("", "", true)}>
          Clear all
        </button>
      </div>
      <ul className={styles.refinementList}>
        {/* Display general_rating only once if present */}
        {generalRatingValues.length > 0 && lowestGeneralRating !== null && (
          <li
            className={`${styles.refinementItem} p-0`}
            key={`general_rating:${lowestGeneralRating}`}
            onClick={() => removeFacet("general_rating", String(lowestGeneralRating), false)}
          >
            <span className={styles.clearRefinement} />
            <span className={styles.refinementLabel}>
              <span className={styles.starRatingsContainer}>
                <StarRating rating={lowestGeneralRating} size="xs" />
                <span className={styles.ratingUpText}>& Up</span>
              </span>
            </span>
          </li>
        )}
        {/* Render all other refinements as before */}
        {otherRefinements.map(([facet, values]) =>
          values.map((value) =>
            shouldExcludeRefinement(facet, value) ? null : (
              <li
                className={`${styles.refinementItem} p-0`}
                key={`${facet}:${value}`}
                onClick={() => removeFacet(facet, value, false)}
              >
                <span className={styles.clearRefinement} />
                <span className={styles.refinementLabel}>{value}</span>
              </li>
            ),
          ),
        )}
      </ul>
    </div>
  ) : (
    <></>
  );
}

type RefinementRecord = {
  facetCategoryName: string;
  facetValueName: string;
};
